import { Subscription } from 'rxjs';

/**
 * A utility class to help manage unsubscribing RxJS subscriptions.
 *
 * In a typical class or component, any subscriptions made must be unsubscribed when the component
 * is destroyed to prevent a memory link. For example, this is typically done in
 * <code>ngOnDestroy()</code> in an Angular component.
 *
 * <pre><code>
 * // Normal cleanup boilerplate
 *
 * @Component({
 *   selector: 'my-component',
 *   templateUrl: 'my.component.html'
 * })
 * export class MyComponent implements OnDestroy {
 *   private clickSub: Subscription;
 *   private modeSub: Subscription;
 *
 *   constructor(click: ClickService, mode: ModeService) {
 *     this.clickSub = click.onClick.subscribe(() => {
 *       // Implementation
 *     });
 *
 *     this.modeSub = mode.changed.subscribe(() => {
 *       // Implementation
 *     });
 *   }
 *
 *   ngOnDestroy() {
 *     if (this.clickSub) {
 *       this.clickSub.unsubscribe();
 *     }
 *
 *     if (this.modeSub) {
 *       this.modeSub.unsubscribe();
 *     }
 *   }
 * }
 *
 * // Cleanup with Subscriptions
 *
 * @Component({
 *   selector: 'my-component',
 *   templateUrl: 'my.component.html'
 * })
 * export class MyComponent implements OnDestroy {
 *   private subs = new Subscriptions();
 *
 *   constructor(click: ClickService, mode: ModeService) {
 *     this.subs.add(click.onClick.subscribe(() => {
 *       // Implementation
 *     }));
 *
 *     this.subs.add(mode.changed.subscribe(() => {
 *       // Implementation
 *     }));
 *   }
 *
 *   ngOnDestroy() {
 *     this.subs.unsubscribe();
 *   }
 * }
 * </code></pre>
 */
export class Subscriptions {
    private subMap: { [name: string]: Subscription[] } = {};

    /**
     * Adds a Subscription to be tracked. Subscriptions may be added with a named group. Groups can
     * be unsubscribed specifically rather than unsubscribing all subscriptions. If no name is
     * provided, the subscription is placed in the "default" group.
     *
     * @param {string|Subscription} nameOrSub the Subscription to track, or the name of the group
     * @param {Subscription=} sub the Subscription to track if specifying a group name
     * @return {Subscription} the tracked Subscription.
     */

    addNamed(name: string, sub: Subscription): Subscription {
        const subs = (this.subMap[name] = this.subMap[name] || []);
        subs.push(sub);
        return sub;
    }

    add(nameOrSub: string | Subscription, maybeSub?: Subscription): Subscription {
        let name: string;
        let sub: Subscription;
        if (typeof nameOrSub === 'string') {
            name = nameOrSub;
            sub = <Subscription>maybeSub;
        } else {
            name = 'default';
            sub = nameOrSub;
        }

        return this.addNamed(name, sub);
    }

    /**
     * Unsubscribes all subscriptions in a specific group name. If no name is provided, all
     * subscriptions in all groups are unsubscribed. The default group name is "default".
     *
     * @param {string=} name optional name of the specific group to unsubscribe
     * @return {number} the number of subscriptions that were unsubscribed.
     */
    unsubscribe(name?: string): number {
        let count = 0;
        if (name) {
            if (this.subMap[name]) {
                this.subMap[name].forEach((sub) => {
                    sub.unsubscribe();
                    count++;
                });

                delete this.subMap[name];
            }
        } else {
            Object.keys(this.subMap).forEach((key) => {
                this.subMap[key].forEach((sub) => {
                    sub.unsubscribe();
                    count++;
                });
            });

            this.subMap = {};
        }

        return count;
    }
}
